import type { Difficulty, FillQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rangesForDifficulty(d: Difficulty): { aMin: number; aMax: number } {
  if (d === 1) return { aMin: 20, aMax: 99 };
  if (d === 2) return { aMin: 100, aMax: 499 };
  return { aMin: 500, aMax: 999 };
}

export function generateDivRemainder(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);
  const { aMin, aMax } = rangesForDifficulty(difficulty);

  // b is 2-9 (divisor, 1-digit non-trivial)
  const b = Math.floor(rand() * 8) + 2; // 2..9

  // Pick a in range; ensure non-zero remainder (retry up to 10 times)
  let a = Math.floor(rand() * (aMax - aMin + 1)) + aMin;
  let attempts = 0;
  while (a % b === 0 && attempts < 10) {
    a = Math.floor(rand() * (aMax - aMin + 1)) + aMin;
    attempts++;
  }
  // If all happen to divide evenly, force a remainder of 1
  if (a % b === 0) a += 1;

  const quotient = Math.floor(a / b);
  const remainder = a % b;

  // Canonical answer format: "X 餘 Y"
  const correctAnswer = `${quotient} 餘 ${remainder}`;
  const acceptedAnswers = [
    correctAnswer,
    `${quotient}餘${remainder}`,
    `${quotient}r${remainder}`,
    `${quotient} r ${remainder}`,
    `${quotient}R${remainder}`,
    `${quotient} R ${remainder}`,
  ];

  return {
    kind: 'fill',
    prompt: `${a} ÷ ${b} = ? 餘 ?`,
    correctAnswer,
    acceptedAnswers,
    explanation: `${b}×${quotient}=${b*quotient},${a}-${b*quotient}=${remainder} → 商 ${quotient} 餘 ${remainder}`,
    kp: 'math.div.remainder',
  };
}
